/**
 * Tracks page sections that can be navigated to via the NavigationBar.
 *
 * While it may seem simpler to discover sections by querying the DOM
 * (e.g. `querySelectorAll` inside a `useEffect`), that approach was tested
 * and led to race conditions on initial load. In some cases, section
 * elements were not yet present in the DOM when the effect ran, causing
 * sections to be missed.
 *
 * To avoid this, each section explicitly registers its ID with this context
 * once it is mounted (See `useNavigableSection` hook).
 */

import { useContext, createContext, useState } from 'react';
import { NAVIGABLE_SECTION_IDS } from '@/components/content/ProductDetails/constants';

type NavigableSectionsContextType = {
	sectionIds: Set<NAVIGABLE_SECTION_IDS>;
	setSectionIds: React.Dispatch<React.SetStateAction<Set<NAVIGABLE_SECTION_IDS>>>;
};

export const NavigableSectionsContext = createContext<NavigableSectionsContextType | null>(null);

export const useNavigableSectionsContext = () => {
	const context = useContext(NavigableSectionsContext);

	if (!context) {
		throw new Error('useNavigableSectionsContext must be used within a NavigableSectionsProvider');
	}

	return context;
};

export const NavigableSectionsProvider = ({ children }: { children: React.ReactNode }) => {
	const [sectionIds, setSectionIds] = useState<Set<NAVIGABLE_SECTION_IDS>>(new Set());

	return (
		<NavigableSectionsContext.Provider
			value={{
				sectionIds,
				setSectionIds,
			}}
		>
			{children}
		</NavigableSectionsContext.Provider>
	);
};
